import type { AddPageArgs, ReplacePageItemsArgs } from "./PagedList";
import { PagedList } from "./PagedList";
import type { ID } from "./types";

/**
 * Alias for {@link AddPageArgs} — the shape `PaginatedNode.addItems`
 * accepts is identical to what its underlying `PagedList.addPage` needs.
 */
export type AddItemsArgs<T = unknown> = AddPageArgs<T>;

export interface PaginatedNodeOptions<T = unknown> {
  id: ID;
  depth: number;
  getItemKey: (item: T) => ID;
  extraData?: Record<string, unknown>;
}

/**
 * Result of `PaginatedNode.removeItem`: whether the item was actually
 * removed plus the removed item's neighbors (as they were *before*
 * removal — used for cursor smoothing).
 */
export interface NodeItemRemovalResult {
  removed: boolean;
  prevId: ID | null;
  nextId: ID | null;
}

/**
 * Dumb container for paginated data at a single tree level.
 *
 * - `pages` holds items for this node (pages of data).
 * - `children` map holds child nodes (managed by controller, not by this class).
 *
 * PaginatedNode does NOT create or delete children — controller owns tree structure.
 */
export class PaginatedNode<T = unknown> {
  readonly id: ID;
  readonly depth: number;
  readonly getItemKey: (item: T) => ID;
  readonly extraData: Record<string, unknown>;

  pages: PagedList<T>;
  private readonly children: Map<ID, PaginatedNode<T>>;
  private _loading: boolean;
  private _justExpanded: boolean;
  private _parent: PaginatedNode<T> | null;

  constructor(options: PaginatedNodeOptions<T>) {
    this.id = options.id;
    this.depth = options.depth;
    this.getItemKey = options.getItemKey;
    this.extraData = options.extraData ?? {};
    this.pages = PagedList.empty<T>(options.getItemKey);
    this.children = new Map();
    this._loading = false;
    this._justExpanded = false;
    this._parent = null;
  }

  /** Parent node, or `null` for the root or detached nodes. */
  get parent(): PaginatedNode<T> | null {
    return this._parent;
  }

  addChild(child: PaginatedNode<T>): void {
    this.children.set(child.id, child);
    child._parent = this;
  }

  removeChild(childId: ID): PaginatedNode<T> | undefined {
    const child = this.children.get(childId);
    if (child) {
      this.children.delete(childId);
    }
    return child;
  }

  getChild(childId: ID): PaginatedNode<T> | undefined {
    return this.children.get(childId);
  }

  hasChild(childId: ID): boolean {
    return this.children.has(childId);
  }

  get hasChildren(): boolean {
    return this.children.size > 0;
  }

  /** Iterate child nodes (insertion order). */
  childValues(): IterableIterator<PaginatedNode<T>> {
    return this.children.values();
  }

  /**
   * Add items to this node's pages.
   * Returns `true` if items were actually added (not all duplicates).
   */
  addItems(args: AddItemsArgs<T>): boolean {
    const prevList = this.pages;
    this.pages = prevList.addPage(args);

    if (this.pages === prevList) {
      return false;
    }

    return true;
  }

  /** Remove a page from specified end. Does NOT touch children — controller handles child subtree cleanup. */
  removeItemsPage(from: "top" | "bottom"): void {
    if (this.pages.isEmpty) {
      return;
    }
    this.pages = this.pages.removePage(from);
  }

  markAfterExhausted(): void {
    this.pages = this.pages.markAfterExhausted();
  }

  markBeforeExhausted(): void {
    this.pages = this.pages.markBeforeExhausted();
  }

  get stale(): boolean {
    return this.pages.hasStalePages;
  }

  markStale(): void {
    this.pages.markAllStale();
  }

  /** Replace items of a single page (by request key). Stale flag auto-cleared by PagedList. */
  replacePageItems(args: ReplacePageItemsArgs<T>): void {
    this.pages = this.pages.replacePageItems(args);
  }

  /**
   * Immutably replace one item in this node's pages.
   * Returns true if the item was found and replaced.
   */
  replaceItem(itemId: ID, newItem: T): boolean {
    const updated = this.pages.replaceItem(itemId, newItem);
    if (updated === this.pages) {
      return false;
    }

    this.pages = updated;
    return true;
  }

  /**
   * Remove one item from this node's pages.
   * Returns neighbors as they were **before** removal (for cursor smoothing).
   */
  removeItem(itemId: ID): NodeItemRemovalResult {
    const { list, prevId, nextId } = this.pages.removeItem(itemId);
    const removed = list !== this.pages;
    if (removed) {
      this.pages = list;
    }
    return { removed, prevId, nextId };
  }

  /**
   * Rewrite request cursors that reference `deletedId` in this node's
   * pages and recursively in all descendant nodes.
   * Returns true if at least one cursor was rewritten.
   */
  rewriteRequestCursorReferences(deletedId: ID, prevId: ID | null, nextId: ID | null): boolean {
    const rewritten = this.pages.rewriteRequestCursor(deletedId, prevId, nextId);
    let changed = rewritten !== this.pages;
    if (changed) {
      this.pages = rewritten;
    }
    for (const child of this.childValues()) {
      if (child.rewriteRequestCursorReferences(deletedId, prevId, nextId)) {
        changed = true;
      }
    }
    return changed;
  }

  /** Reset this node: clear pages and all children. */
  clearItems(): void {
    this.pages = PagedList.empty<T>(this.getItemKey);
    this.children.clear();
    this._loading = false;
    this._justExpanded = false;
  }

  /**
   * Reset this node's pages back to a fresh, bootstrap-eligible state
   * (clears any sticky exhaustion overlay and stale flags), without touching
   * children. Used by `invalidateLoadedData` so a previously-empty or
   * exhausted-empty node can re-bootstrap on the next planner cycle.
   */
  resetForBootstrap(): void {
    this.pages = PagedList.empty<T>(this.getItemKey);
  }

  /**
   * Whether a fetch for this node is currently in flight. The controller
   * sets this to `true` while it's processing a `LoadRequest` against
   * this node and back to `false` when the request finishes (applied,
   * skipped, or aborted). The branch projection uses it to distinguish
   * "empty awaiting bootstrap" (chain stops, header acts as the
   * bootstrap slot) from "empty actively loading" (chain continues —
   * siblings stay visible so the consumer's layout doesn't shrink while
   * records are in transit).
   */
  get loading(): boolean {
    return this._loading;
  }

  markLoading(): void {
    this._loading = true;
  }

  markLoaded(): void {
    this._loading = false;
    this._justExpanded = false;
  }

  /**
   * Whether this node was just expanded (collapsed → uncollapsed
   * transition observed by `cleanCollapsed`) and has not yet received
   * its first fetch result. Used by `isEmptyAwaitingFromLast` to opt
   * out of the scroll-up `fromLast` redirect — on expand, the user
   * expects a forward bootstrap (first page), not the last page.
   *
   * Cleared on the first `markLoaded` after the bootstrap fetch (or on
   * `clearItems` if the user re-collapses the node).
   */
  get justExpanded(): boolean {
    return this._justExpanded;
  }

  markJustExpanded(): void {
    this._justExpanded = true;
  }
}
